"use client";

import {
  ChevronDownIcon,
  CircleUserRoundIcon,
  LogOutIcon,
  TicketIcon,
} from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@tripcart/traveler-ui/components/dropdown-menu";

import { useTranslator } from "@/lib/translation-context";

import styles from "./user-avatar-menu.module.css";

function AvatarGlyph({
  initials,
  avatarUrl,
}: {
  initials: string;
  avatarUrl?: string | null;
}): React.ReactElement {
  return (
    <span className={styles.avatar}>
      {avatarUrl ? (
        <Image
          src={avatarUrl}
          alt=""
          fill
          sizes="32px"
          className={styles.avatarImage}
        />
      ) : (
        initials
      )}
    </span>
  );
}

/**
 * Avatar dropdown for the signed-in state. Uses the verified working
 * pattern from operator-web's WorkspaceCard: `onSelect` + plain
 * children, no render target, no controlled `open` state.
 *
 * Row geometry comes from the primitive, which carries the style guide's
 * Menu Spec. Only the menu's own width is set here; the `variant="popup"`
 * and per-item padding overrides that used to live here were the elevation
 * and spacing the primitive now ships.
 *
 * Auth: sign-out is a NAVIGATION to /sign-out, not an action fired from
 * here. This menu is rendered into the header of every page, and the
 * storefront tree reads no session client-side on purpose, so nothing here
 * may either. Keeping this component free of the browser auth client is what
 * lets browse pages ship zero auth JS (BP 78 → ~100, ~300KB less bundle on
 * every browse page); the
 * provider is scoped to /sign-out, which is where the session actually ends.
 */
export function UserAvatarMenu({
  initials,
  name,
  avatarUrl,
}: {
  initials: string;
  name?: string | null;
  email?: string | null;
  /** This agency's photo for the signed-in traveller — see
   *  `lib/customer-avatar.ts`. Null or unset shows the initials, same as
   *  before this prop existed. */
  avatarUrl?: string | null;
}): React.ReactElement {
  const router = useRouter();
  const t = useTranslator();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t("Open Account Menu")}
        className={styles.trigger}
      >
        <AvatarGlyph initials={initials} avatarUrl={avatarUrl} />
        {name ? <span className={styles.name}>{name}</span> : null}
        <ChevronDownIcon
          className={styles.chevron}
          strokeWidth={1.75}
          aria-hidden
        />
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        sideOffset={14}
        className={styles.content}
      >
        <DropdownMenuItem onSelect={() => router.push("/profile/")}>
          <CircleUserRoundIcon
            className={styles.icon}
            strokeWidth={1.75}
            aria-hidden
          />
          <span className={styles.label}>{t("Profile")}</span>
        </DropdownMenuItem>

        <DropdownMenuItem onSelect={() => router.push("/bookings/")}>
          <TicketIcon className={styles.icon} strokeWidth={1.75} aria-hidden />
          <span className={styles.label}>{t("Bookings")}</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          // Navigate, don't act. This menu renders in the header of EVERY
          // page, and the server action it replaces could not read the session
          // there — it threw before revoking anything or clearing a single
          // cookie, so the traveller got an error page and stayed signed in.
          // `/sign-out` runs it somewhere it can actually happen.
          //
          // A FULL load, not `router.push`, unlike its siblings above. A soft
          // navigation issues an `?_rsc=` request, and an HTML answer to one
          // of those is `Expected RSC response, got text/html` → 500 → hard
          // reload, the class this repo has hit
          // arise, but this is the LOGOUT path: a transient error page here is
          // exactly what convinces someone they are signed out when they are
          // not. Belt and braces, and it costs one navigation.
          onSelect={() => window.location.assign("/sign-out/")}
        >
          <LogOutIcon className={styles.icon} strokeWidth={1.75} aria-hidden />
          <span className={styles.label}>{t("Log Out")}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
